// src/pages/Pages.js
// Lists DAJC pages — auto-filtered to service 5 via /api/serviceportail/edit/{id}
import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";

import BASE_URL, { DAJC } from "../config";
import {
  getTitle, getImage, getExcerpt, localePath,
} from "../utils/content";
import Pagination from "../components/Pagination";
import Seo from "../components/Seo";

/** Inline SVG fallback for pages with no image */
function PageImageFallback({ title = "" }) {
  const initials = title
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");

  return (
    <div className="card-article__media-fallback" aria-hidden="true">
      <svg
        viewBox="0 0 320 200"
        xmlns="http://www.w3.org/2000/svg"
        width="100%"
        height="100%"
        style={{ display: "block" }}
      >
        <defs>
          <linearGradient id="pageFallbackGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#1a1200" stopOpacity="1" />
            <stop offset="100%" stopColor="#2c1f00" stopOpacity="1" />
          </linearGradient>
        </defs>
        <rect width="320" height="200" fill="url(#pageFallbackGrad)" />

        {/* Document lines decorative */}
        <g opacity="0.15" stroke="var(--gold, #c9a84c)" strokeWidth="1.5" fill="none">
          <rect x="100" y="30" width="80" height="100" rx="4" />
          <line x1="112" y1="55" x2="168" y2="55" />
          <line x1="112" y1="68" x2="168" y2="68" />
          <line x1="112" y1="81" x2="155" y2="81" />
          <line x1="112" y1="94" x2="162" y2="94" />
          <line x1="112" y1="107" x2="150" y2="107" />
          <path d="M160 30 l20 20 l-20 0 Z" fill="var(--gold, #c9a84c)" opacity="0.3" />
        </g>

        {/* Corner accents */}
        <line x1="0" y1="0" x2="32" y2="0" stroke="var(--gold, #c9a84c)" strokeWidth="2" opacity="0.45" />
        <line x1="0" y1="0" x2="0" y2="32" stroke="var(--gold, #c9a84c)" strokeWidth="2" opacity="0.45" />
        <line x1="320" y1="200" x2="288" y2="200" stroke="var(--gold, #c9a84c)" strokeWidth="2" opacity="0.45" />
        <line x1="320" y1="200" x2="320" y2="168" stroke="var(--gold, #c9a84c)" strokeWidth="2" opacity="0.45" />

        {/* Initials */}
        {initials ? (
          <text
            x="160"
            y="115"
            textAnchor="middle"
            dominantBaseline="middle"
            fill="var(--gold, #c9a84c)"
            fontSize="40"
            fontFamily="Georgia, serif"
            letterSpacing="6"
            opacity="0.85"
          >
            {initials}
          </text>
        ) : (
          <text
            x="160"
            y="115"
            textAnchor="middle"
            dominantBaseline="middle"
            fill="var(--gold, #c9a84c)"
            fontSize="44"
            opacity="0.65"
          >
            📄
          </text>
        )}
      </svg>
    </div>
  );
}

export default function Pages() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [pages, setPages] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [perPage, setPerPage] = useState(9);
  const [debounced, setDebounced] = useState("");

  const cancelRef = useRef(null);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(searchTerm), 400);
    return () => clearTimeout(id);
  }, [searchTerm]);

  useEffect(() => { setPage(1); }, [debounced, perPage]);

  useEffect(() => {
    if (cancelRef.current) cancelRef.current.cancel();
    cancelRef.current = axios.CancelToken.source();
    setLoading(true);

    axios
      .get(`${BASE_URL}/api/serviceportail/edit/${DAJC.SERVICE_ID}`, {
        params: { page, per_page: perPage, search: debounced },
        cancelToken: cancelRef.current.token,
      })
      .then((r) => {
        setPages(r.data?.pages?.data || []);
        setTotalPages(r.data?.pages?.pagination?.last_page || 1);
        setLoading(false);
      })
      .catch((err) => {
        if (!axios.isCancel(err)) setLoading(false);
      });
  }, [page, perPage, debounced]);

  return (
    <>
      <Seo
        path="ressources"
        title={t("pages.title")}
        description={t("pages.subtitle")}
        breadcrumb={[
          { name: t("common.home"), path: "" },
          { name: t("menu.ressources"), path: "ressources" },
        ]}
      />

      <section className="page-header">
        <div className="container">
          <div className="page-header__inner">
            <div className="page-header__crumbs">
              <Link to={localePath(lang)}>{t("common.home")}</Link>
              <span>/</span>
              <span>{t("menu.ressources")}</span>
            </div>
            <h1>{t("pages.title")}</h1>
            <p>{t("pages.subtitle")}</p>
          </div>
        </div>
      </section>

      <section style={{ background: "var(--ivory)" }}>
        <div className="container">
          <div className="filter-bar">
            <div className="filter-bar__search">
              <i className="bi bi-search" />
              <input
                type="text"
                placeholder={t("search.placeholder")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button onClick={() => setSearchTerm("")} aria-label="Clear">
                  <i className="bi bi-x-lg" />
                </button>
              )}
            </div>
            <select value={perPage} onChange={(e) => setPerPage(Number(e.target.value))}>
              <option value={6}>6 / {t("search.perPage")}</option>
              <option value={9}>9 / {t("search.perPage")}</option>
              <option value={12}>12 / {t("search.perPage")}</option>
              <option value={24}>24 / {t("search.perPage")}</option>
            </select>
          </div>

          {loading ? (
            <div className="loading"><div className="spinner" /></div>
          ) : pages.length === 0 ? (
            <div className="empty">
              <h3>{t("search.noResults")}</h3>
              <p>{t("search.tryDifferentTerms")}</p>
            </div>
          ) : (
            <div className="cards-grid">
              {pages.map((p) => {
                const img = getImage(p, lang);
                const title = getTitle(p, lang, 80);
                return (
                  <article key={p.id} className="card-article">
                    <div className="card-article__media">
                      {img ? (
                        <img
                          src={img}
                          alt={title}
                          loading="lazy"
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                            e.currentTarget.nextSibling?.style &&
                              (e.currentTarget.nextSibling.style.display = "flex");
                          }}
                        />
                      ) : null}
                      <PageImageFallback title={title} />
                    </div>

                    <div className="card-article__body">
                      <div className="card-article__date">{t("pages.consult")}</div>
                      <h3 className="card-article__title">
                        <Link to={localePath(lang, `page/${p.id}/${p.slug || ""}`)}>
                          {title}
                        </Link>
                      </h3>
                      <p className="card-article__excerpt">{getExcerpt(p, lang, 22)}</p>
                      <Link
                        to={localePath(lang, `page/${p.id}/${p.slug || ""}`)}
                        className="card-article__more"
                      >
                        {t("articles.read_more")}
                      </Link>
                    </div>
                  </article>
                );
              })}
            </div>
          )}

          {totalPages > 1 && (
            <Pagination current={page} total={totalPages} onChange={setPage} />
          )}
        </div>
      </section>
    </>
  );
}